import { Calculator, Eye, Lock, Users } from 'lucide-react'
import { Helmet } from 'react-helmet'

import truckHeroImg from '@/assets/marketing/images/truck-exiting-lot.png'
import { Button } from '@/components/ui/button'
import { appHref } from '@/config/config'
import { trackMarketingCta } from '@/lib/analytics'
import { Reveal } from './components/Reveal'
import { SafetySnapshotCard } from './components/SafetySnapshotCard'
import { StepCard } from './components/StepCard'
import { ValueProp } from './components/ValueProp'

const STATS = [
  { value: '1', label: 'DOT number to start an application' },
  { value: '10', label: 'Insurance carriers shopped at once' },
  { value: '24/7/365', label: 'Support behind your human broker' },
]

const STEPS = [
  {
    number: '01',
    title: 'Easier applications',
    body: 'No more answering the same question 10 ways. Cai pulls your fleet details from your DOT and any docs you upload. You review before anything is submitted.',
  },
  {
    number: '02',
    title: 'Shopping around',
    body: 'One broker can only call so many providers. Cai applies to up to 10 insurance companies at once, and focuses on the ones that can actually quote you well.',
  },
  {
    number: '03',
    title: 'Always-on support',
    body: 'Trucking does not sleep. If a driver is in an accident at 3AM, Cai gets the details, contacts your insurance company, and loops in your broker.',
  },
  {
    number: '04',
    title: 'Instant COIs',
    body: 'A COI now versus in a few hours can mean winning or losing a load. Cai produces COIs instantly.',
  },
  {
    number: '05',
    title: 'Compliance monitoring',
    body: 'Cai tracks your ELD data and FMCSA record year-round and flags fixes you can make today to avoid a premium increase later.',
  },
]

const NOT_AI = [
  {
    key: 'broker',
    icon: <Users />,
    title: 'Replacing your broker',
    body: 'AI is the assistant. Your broker is still the person you call.',
  },
  {
    key: 'eye',
    icon: <Eye />,
    title: 'Hiding information',
    body: 'You see every quote we get back — and the reason behind any denial.',
  },
  {
    key: 'lock',
    icon: <Lock />,
    title: 'Sharing your chats',
    body: 'Your conversations with Cai are between you and Cai.',
  },
  {
    key: 'math',
    icon: <Calculator />,
    title: 'Crunching the numbers',
    body: 'AI can make mistakes. We’ll calculate pricing to make sure it adds up.',
  },
]

export function HowWeUseAi() {
  return (
    <>
      <Helmet>
        <title>How We Use AI</title>
      </Helmet>
      <meta
        name="description"
        content="We ran our own fleet. We know how slow and painful trucking insurance can be. And we think AI is part of the solution."
      />

      <section className="bg-surface px-5.5 pt-24 pb-10">
        <div className="mx-auto flex max-w-(--maxw) flex-col gap-8">
          <Reveal>
            <span className="eyebrow hero-eyebrow">Inside Cai</span>
          </Reveal>
          <div className="grid grid-cols-2 items-end gap-8 max-lg:grid-cols-1">
            <Reveal className="duration-[900ms]" delay={400}>
              <h1 className="h1-hero text-balance">
                How Cai uses AI for{' '}
                <span className="text-brand-800">insurance that doesn&apos;t hold you back</span>
              </h1>
            </Reveal>
            <Reveal className="duration-[900ms]" delay={800}>
              <p className="text-ink-500 pb-1.5 text-xl leading-[1.6] text-pretty">
                We ran our own fleet. We know how slow and painful trucking insurance can be. And we think AI
                is part of the solution.
              </p>
            </Reveal>
          </div>
          <Reveal className="duration-[900ms]" delay={1200}>
            <div className="relative aspect-4/3 max-h-90 w-full overflow-hidden rounded-sm lg:aspect-video lg:max-h-130">
              <img
                src={truckHeroImg}
                alt="Aerial view of a truck pulling out of a loading dock"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-surface px-5.5 pb-10 lg:pb-20">
        <div className="mx-auto max-w-(--maxw)">
          <div className="border-ink-100 grid grid-cols-3 gap-x-10 border-t border-b max-md:grid-cols-1">
            {STATS.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-2 py-8">
                <div className="font-mono text-5xl font-bold tracking-[-0.02em]">{stat.value}</div>
                <div className="text-md text-ink-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="uses" className="bg-ink-900 px-5.5 py-24 lg:py-32">
        <div className="mx-auto flex max-w-(--maxw) flex-col gap-12">
          <Reveal className="border-ink-700/60 flex flex-wrap items-end justify-between gap-10 border-b pb-6">
            <div className="flex flex-col gap-4">
              <span className="text-brand-300 font-mono text-xs tracking-[0.14em] uppercase">
                Where AI helps
              </span>
              <h2 className="h2 text-white">5 ways AI improves insurance</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1">
            {STEPS.map((step, i) => (
              <Reveal key={step.number} className="h-full" delay={i * 100}>
                <StepCard className="h-full" number={step.number} title={step.title}>
                  {step.body}
                </StepCard>
              </Reveal>
            ))}
            <Reveal className="h-full" delay={STEPS.length * 100}>
              <SafetySnapshotCard />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-surface px-5.5 py-24 lg:py-32">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-[1fr_1.3fr] gap-x-16 gap-y-10 max-md:grid-cols-1">
          <Reveal className="flex flex-col gap-4">
            <span className="text-ink-500 font-mono text-xs tracking-[0.14em] uppercase">
              Where it stays out
            </span>
            <h2 className="h2 max-w-[342px]">How Cai does NOT use AI</h2>
            <p className="text-ink-500 text-md max-w-[34ch]">Your broker is still a person, not a bot.</p>
          </Reveal>
          <div className="grid grid-cols-2 gap-x-12 gap-y-16 max-sm:grid-cols-1">
            {NOT_AI.map((prop, i) => (
              <Reveal key={prop.key} delay={i * 100}>
                <ValueProp icon={prop.icon} title={prop.title}>
                  {prop.body}
                </ValueProp>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="bg-ink-50 px-5.5 py-24 lg:py-32">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-2 items-end gap-x-16 gap-y-10 max-md:grid-cols-1">
          <Reveal>
            <h2 className="h2 max-w-[14ch]">Make your fleet&apos;s insurance more intelligent.</h2>
          </Reveal>
          <Reveal className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                size="lg"
                href={appHref('/application/start')}
                onClick={() =>
                  trackMarketingCta('how_we_use_ai_start_application', {
                    destination: appHref('/application/start'),
                  })
                }
              >
                Start an application
              </Button>
              <Button
                variant="outline"
                size="lg"
                href="/insurance"
                onClick={() => trackMarketingCta('how_we_use_ai_learn_more', { destination: '/insurance' })}
              >
                Learn more
              </Button>
            </div>
            <p className="text-ink-500 border-ink-200 text-md border-t pt-6 leading-[1.55]">
              Want to learn more about how we do and don&apos;t use AI?{' '}
              <a
                href="https://blog.cai.ai/how-cai-is-using-ai-to-fix-trucking-insurance"
                className="text-ink-500 hover:text-ink border-ink-500/35 hover:border-ink border-b pb-0.5 no-underline transition-colors duration-150"
                onClick={() => trackMarketingCta('how_we_use_ai_blog_post')}
              >
                Read the full blog post
              </a>{' '}
              (written by a human).
            </p>
          </Reveal>
        </div>
      </section>
    </>
  )
}
